import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  Code,
  Palette,
  Smartphone,
  Globe,
  Github,
  Linkedin,
  Mail,
  ExternalLink,
} from "lucide-react";

import heroImage from "./assets/hero-background.jpg";
import ecommerceImage from "./assets/ecommerce.jpg";
import socialImage from "./assets/social.jpg";
import logo from "./assets/logo.png";
import skilllink from "./assets/skilllink.png";
import marketprice from "./assets/marketprice.png";

const CheckCircleIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const SparklesIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
    />
  </svg>
);

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  const PAYSTACK_PUBLIC_KEY =
    "pk_live_755c52be93e9db58f39401f0ecb9d20df9e0cdbe";

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections((prev) => new Set([...prev, entry.target.id]));
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );
    const sections = document.querySelectorAll("[data-animate]");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const handlePayment = (plan) => {
    if (!paystackLoaded) {
      alert("Payment system is loading. Please try again in a moment.");
      return;
    }

    const email = prompt("Please enter your email address:");
    if (!email) return;

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: email,
      amount: plan.price * 100,
      currency: "NGN",
      ref: "TSX_" + Math.floor(Math.random() * 1000000000 + 1),
      metadata: {
        custom_fields: [
          {
            display_name: "Package",
            variable_name: "package",
            value: plan.title,
          },
        ],
      },
      callback: function (response) {
        alert(
          "Payment successful! Reference: " +
            response.reference +
            "\n\nYou will receive an email shortly with booking details.",
        );
      },
      onClose: function () {
        console.log("Payment window closed.");
      },
    });
    handler.openIframe();
  };

  const bookingPlans = [
    {
      title: "Single Session",
      price: 10000,
      duration: "/hour",
      features: [
        "One-on-one tutorial",
        "Personalized learning plan",
        "Screen sharing & collaboration",
        "Session recording available",
        "Email support for 48 hours",
      ],
      popular: false,
    },
    {
      title: "5-Session Package",
      price: 50000,
      duration: "/package",
      features: [
        "Five 1-hour sessions",
        "Comprehensive curriculum",
        "Priority scheduling",
        "All session recordings",
        "Ongoing email support",
        "Progress tracking & feedback",
        "Save ₦10,000 compared to singles",
      ],
      popular: true,
    },
    {
      title: "Monthly Mentorship",
      price: 100000,
      duration: "/month",
      features: [
        "8 hours of tutorial time",
        "Flexible scheduling",
        "Project review & guidance",
        "Code review sessions",
        "24/7 chat support",
        "Career counseling included",
        "Best value for serious learners",
      ],
      popular: false,
    },
  ];

  const skills = [
    { name: "React & Next.js", icon: <Code className="w-6 h-6" />, level: 95 },
    { name: "UI/UX Design", icon: <Palette className="w-6 h-6" />, level: 90 },
    {
      name: "Mobile Development",
      icon: <Smartphone className="w-6 h-6" />,
      level: 85,
    },
    { name: "Full Stack", icon: <Globe className="w-6 h-6" />, level: 92 },
  ];

  const projects = [
    {
      title: "E-Commerce Platform -- TasknCart",
      category: "Full Stack",
      description:
        "A modern e-commerce platform built with React, Node.js, and MongoDB featuring real-time inventory management.",
      tech: ["React", "Node.js", "MongoDB", "Stripe"],
      image: ecommerceImage,
    },

    {
      title: "Mobile Banking App",
      category: "Mobile",
      description:
        "Secure mobile banking application with biometric authentication and instant transfers.",
      tech: ["React Native", "Firebase", "Plaid API"],
      image:
        "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop",
    },
    {
      title: "Social Media Platform",
      category: "Full Stack",
      description:
        "Real-time social networking platform with chat, posts, and content sharing features.",
      tech: ["Next.js", "PostgreSQL", "WebSocket", "AWS"],
      image: socialImage,
    },
    {
      title: "Market Price TNC",
      category: "Full Stack",
      description:
        "A data-driven web application for tracking and comparing real-time food price trends across markets.Real-time social networking platform with chat, posts, and content sharing features.",
      tech: ["Next.js", "PostgreSQL", "WebSocket", "AWS"],
      image: marketprice,
    },
    {
      title: "Freelance Platform",
      category: "Full Stack",
      description:
        "Connects Skilled Profesionals with Clients Worldwide for Project-Based Work.",
      tech: ["Next.js", "PostgreSQL", "WebSocket", "AWS"],
      image: skilllink,
    },
    {
      title: "AI-Powered Dashboard",
      category: "Frontend",
      description:
        "Interactive analytics dashboard with AI-driven insights and beautiful data visualizations.",
      tech: ["React", "D3.js", "TailwindCSS", "AI APIs"],
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
    },
  ];

  const testimonials = [
    {
      name: "Mz. Sarah",
      role: "CEO, TnC Inc",
      text: "Sixtus delivered exceptional work on our platform. His attention to detail and technical expertise transformed our vision into reality.",
      avatar: "MS",
    },
    {
      name: "Mr. Michael",
      role: "Product Manager, InnovateCo",
      text: "Working with Sixtus was a game-changer. He not only built what we asked for but improved it with innovative solutions.",
      avatar: "MM",
    },
    {
      name: "James P.h.d",
      role: "Founder, StartupHub",
      text: "The quality of code and user experience Sixtus creates is outstanding. Highly recommend for any web project!",
      avatar: "JP",
    },
  ];

  const heroBackgroundImage = heroImage;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-950 to-black">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-gray-900/95 backdrop-blur-md shadow-lg shadow-blue-500/10" : "bg-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2">
              <img
                src={logo}
                alt="Tech Sixtus Logo"
                className="h-12 w-auto object-contain"
              />
            </div>

            <div className="hidden md:flex space-x-8">
              {[
                "Home",
                "About",
                "Booking",
                "Skills",
                "Projects",
                "Contact",
              ].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="text-gray-300 hover:text-blue-400 transition-colors duration-300 font-medium relative group"
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-400 group-hover:w-full transition-all duration-300"></span>
                </a>
              ))}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2 hover:bg-blue-500/10 rounded-lg transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-900/98 backdrop-blur-md border-t border-blue-500/20">
            <div className="px-4 py-6 space-y-4">
              {[
                "Home",
                "About",
                "Booking",
                "Skills",
                "Projects",
                "Contact",
              ].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-gray-300 hover:text-blue-400 transition-colors duration-300 font-medium py-2"
                >
                  {item}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      <section
        id="home"
        className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          {heroBackgroundImage ? (
            <img
              src={heroBackgroundImage}
              alt="Hero Background"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-900/20 via-gray-900/40 to-black/60"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-gray-900/90 to-black/95"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div
              className="space-y-8 opacity-0 animate-fadeInUp"
              style={{ animation: "fadeInUp 1s ease-out forwards" }}
            >
              <div className="inline-block">
                <span className="bg-blue-600/20 text-blue-300 px-4 py-2 rounded-full text-sm font-medium border border-blue-400/30 shadow-lg shadow-blue-500/20">
                  Available for Freelance
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
                Building Digital
                <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {" "}
                  Experiences
                </span>
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed">
                Full-stack developer & UI/UX designer crafting beautiful,
                functional web applications that users love. Specializing in
                React, Node.js, and modern web technologies.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#booking"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 inline-flex items-center gap-2"
                >
                  Book a Session
                  <SparklesIcon className="w-5 h-5" />
                </a>
                <a
                  href="#projects"
                  className="border-2 border-blue-400 text-blue-300 px-8 py-4 rounded-lg font-semibold hover:bg-blue-500/10 transition-all duration-300"
                >
                  View My Work
                </a>
              </div>

              <div className="flex gap-6 pt-4">
                <a
                  href="https://github.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                >
                  <Github className="w-6 h-6" />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                >
                  <Linkedin className="w-6 h-6" />
                </a>
                <a
                  href="mailto:techsixtus@gmail.com"
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                >
                  <Mail className="w-6 h-6" />
                </a>
              </div>
            </div>

            <div
              className="relative opacity-0 animate-fadeInUp"
              style={{
                animation: "fadeInUp 1s ease-out 0.3s forwards",
                animationFillMode: "both",
              }}
            >
              <div className="bg-gradient-to-br from-blue-900/40 to-gray-900/40 rounded-2xl p-8 backdrop-blur-sm border border-blue-500/30 shadow-2xl shadow-blue-500/20">
                <pre className="text-blue-300 text-sm overflow-x-auto">
                  <code>{`const developer = {
  name: "Tech Sixtus",
  skills: [
    "React", "Node.js", 
    "MongoDB", "TypeScript"
  ],
  passion: "Building Amazing Apps",
  available: true,
  
  buildDreams() {
    return "Let's create something
            incredible together!";
  }
};

console.log(developer.buildDreams());`}</code>
                </pre>
              </div>

              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full blur-3xl opacity-40"></div>
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full blur-3xl opacity-40"></div>
            </div>
          </div>

          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 opacity-0 animate-fadeInUp"
            style={{
              animation: "fadeInUp 1s ease-out 0.6s forwards",
              animationFillMode: "both",
            }}
          >
            {[
              { label: "Projects Completed", value: "50+" },
              { label: "Happy Clients", value: "30+" },
              { label: "Years Experience", value: "5+" },
              { label: "Code Commits", value: "10K+" },
            ].map((stat, i) => (
              <div
                key={i}
                className="text-center bg-gray-900/40 backdrop-blur-sm border border-blue-500/20 rounded-xl p-4"
              >
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-gray-400 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="about"
        data-animate
        className={`py-20 px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible("about") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              👋 Hi, I’m a Full-Stack Web Developer
            </h2>
            <p className="text-xl text-gray-400">
              I build fast, scalable, and user-focused web applications for
              startups and businesses. From dashboards and data platforms to
              e-commerce systems and admin panels, I turn ideas into
              production-ready products. Tech Stack: React · JavaScript ·
              Node.js · Express · MongoDB · REST APIs · Tailwind CSS · Chart.js
              · Git · GitHub · Netlify · Vercel Let’s build something that works
              — and scales. 🚀
            </p>
          </div>
        </div>
      </section>

      <section
        id="booking"
        data-animate
        className={`py-20 px-4 sm:px-6 lg:px-8 bg-black/30 transition-all duration-1000 ${isVisible("booking") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Book{" "}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Tutorial Services
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              Professional coaching and mentorship tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {bookingPlans.map((plan, i) => (
              <div
                key={i}
                className={`relative bg-gray-900/50 backdrop-blur-sm border ${plan.popular ? "border-blue-500 shadow-xl shadow-blue-500/30" : "border-blue-500/30"} rounded-2xl p-8 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-500 transform hover:-translate-y-2 ${isVisible("booking") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">
                    {plan.title}
                  </h3>
                  <div className="text-5xl font-bold text-white mb-2">
                    ₦{plan.price.toLocaleString()}
                    <span className="text-lg text-gray-400 font-normal">
                      {plan.duration}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-gray-300"
                    >
                      <CheckCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePayment(plan)}
                  className={`w-full py-4 rounded-lg font-semibold transition-all duration-300 ${plan.popular ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-500/50 hover:scale-105" : "bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/30 hover:scale-105"}`}
                >
                  Book Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="skills"
        data-animate
        className={`py-20 px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible("skills") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Technical Expertise
            </h2>
            <p className="text-xl text-gray-400">
              Technologies I work with to bring ideas to life
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {skills.map((skill, i) => (
              <div
                key={i}
                className={`bg-gray-900/50 backdrop-blur-sm border border-blue-500/30 rounded-xl p-6 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-500 group ${isVisible("skills") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {skill.icon}
                </div>
                <h3 className="text-white font-semibold text-lg mb-3">
                  {skill.name}
                </h3>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 h-2 rounded-full transition-all duration-1000"
                    style={{
                      width: isVisible("skills") ? `${skill.level}%` : "0%",
                    }}
                  ></div>
                </div>
                <div className="text-gray-400 text-sm mt-2">
                  {skill.level}% Proficiency
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="projects"
        data-animate
        className={`py-20 px-4 sm:px-6 lg:px-8 bg-black/30 transition-all duration-1000 ${isVisible("projects") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Projects
            </h2>
            <p className="text-xl text-gray-400">
              Recent work that showcases my capabilities
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, i) => (
              <div
                key={i}
                className={`bg-gray-900/50 backdrop-blur-sm border border-blue-500/30 rounded-xl overflow-hidden hover:border-blue-400 hover:shadow-xl hover:shadow-blue-500/20 transition-all duration-500 group ${isVisible("projects") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="relative overflow-hidden h-64">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm shadow-lg">
                    {project.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-3">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, j) => (
                      <span
                        key={j}
                        className="bg-blue-600/20 text-blue-300 px-3 py-1 rounded-full text-sm border border-blue-500/30"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <button className="text-blue-400 hover:text-blue-300 font-semibold inline-flex items-center gap-2 group">
                    .
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-animate
        className={`py-20 px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible("projects") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Client Says
            </h2>
            <p className="text-xl text-gray-400">
              What people say about working with me
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <div
                key={i}
                className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/30 rounded-xl p-6 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-500"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-blue-500/30">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">
                      {testimonial.name}
                    </h4>
                    <p className="text-gray-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-300 italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="contact"
        data-animate
        className={`py-20 px-4 sm:px-6 lg:px-8 bg-black/30 transition-all duration-1000 ${isVisible("contact") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Have a project in mind? Let's create something amazing together!
          </p>

          <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-500/30 rounded-xl p-8 md:p-12 shadow-xl shadow-blue-500/10">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <input
                type="text"
                placeholder="Your Name"
                className="bg-gray-800/50 border border-blue-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="bg-gray-800/50 border border-blue-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <textarea
              placeholder="Tell me about your project..."
              rows={6}
              className="w-full bg-gray-800/50 border border-blue-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all mb-6"
            ></textarea>
            <button className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-12 py-4 rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 w-full md:w-auto">
              Send Message
            </button>
          </div>

          <div className="flex justify-center gap-6 mt-12">
            <a
              href="mailto:techsixtus@gmail.com"
              className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
            >
              <Mail className="w-8 h-8" />
            </a>
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
            >
              <Github className="w-8 h-8" />
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
            >
              <Linkedin className="w-8 h-8" />
            </a>
          </div>
        </div>
      </section>

      <footer className="py-8 px-4 border-t border-blue-500/20">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            © 2026 Tech Sixtus. All rights reserved.
          </p>
        </div>
      </footer>
      <style>{`
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `}</style>
    </div>
  );
}
export default App;
